// 메인 채팅 페이지 — 바닐라 app.js 코어 루프 이식 (Phase 3-A):
// send()/handleEvent()(SSE 분기), 진행 라벨·이전 단계 접기, 토큰 rAF 배칭 스트리밍,
// [[TITLE]]/[[CHOICES]]/[[NEXT]] 마커, 대화 세션(스냅샷 호환 저장·서버 병합 동기화),
// 딥링크 /c/:id(라우터), sticky bottom 스크롤, 워치독(postSse 내장 60s).
//
// Phase 3-B 추가: 차트 렌더(lib/vachart hydrate·테마 refresh), 우측 정보 패널
// (targets·traceGroups)·결과 패널(screen_panel), 빠른 실행 ⚡·전체 qcards,
// 파일 첨부(칩·드래그앤드롭·FormData files), 문서함, qnav, 사이드바 완성
// (그룹·고정·이름변경·링크복사·접기/리사이저), 상단 제목 ⋯ 메뉴.
//
// 스냅샷(messagesHtml) 호환: MessageList 가 바닐라와 동일 클래스 구조로 렌더하므로
// 그 innerHTML 을 그대로 저장한다 — 바닐라 openSession(innerHTML 복원)과 양방향 호환.
import { isEnterpriseOnlyPath, isEnterpriseUser } from "../lib/enterprise";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams , useLocation } from "react-router-dom";
import Composer from "../components/chat/Composer";
import FilePreviewPanel, {
  type PreviewFile,
} from "../components/chat/FilePreviewPanel";
import InfoPanel from "../components/chat/InfoPanel";
import LibraryView from "../components/chat/LibraryView";
import MessageList from "../components/chat/MessageList";
import ResultsPanel, { type ScreenSpec } from "../components/chat/ResultsPanel";
import {
  RIGHT_PANEL_CLOSED,
  effectiveTab,
  isPanelVisible,
  rightPanelReduce,
  showTabBar,
  type RightPanelEvent,
  type RightPanelState,
} from "../components/chat/rightPanel";
import SessionMenu from "../components/chat/SessionMenu";
import ContactFooter from "../components/ContactFooter";
import Sidebar from "../components/chat/Sidebar";
import SideReopen from "../components/chat/SideReopen";
import { initialCollapsed, isMobile, useMobileSide } from "../lib/useMobileSide";
import { Button } from "../ui/Button";
import {
  renderArtifactHtml,
  type ChatArtifact,
} from "../components/chat/artifactHtml";
import {
  fmtElapsed,
  newAgentTurn,
  type AgentTurn,
  type ChatTurn,
  type ProgressLine,
} from "../components/chat/types";
import { api, authHeaders, gatewayMessage } from "../lib/api";
import {
  cleanCompanyName,
  extractChoices,
  finishedPhrase,
  normalizeNextChip,
  nowTime,
  progressKey,
  stripMarkers,
  summarizeTitle,
  type VaTarget,
  formatToolCall,
} from "../lib/chatText";
import { mdLite } from "../lib/markdown";
import { postSse, type SseEvent } from "../lib/sse";
import { toolLabel } from "../lib/toolLabels";
import { shellClass, useRailSettle } from "../lib/useRailSettle";
import { VAChart } from "../lib/vachart";
import { DECLINER_FALLBACK, DISCOVERY_ALT, HERO_CARDS, fillDecliner } from "../lib/suggestions";
import { greetingTitle, pickGreeting } from "../lib/greetings";
import { GRAPH_CHIPS, GRAPH_CONV_PREFIX, isGraphConv } from "../lib/graphChat";
import { useAuth } from "../stores/auth";
import {
  useChat,
  type HistMsg,
  type TraceGroup,
  type VaSession,
} from "../stores/chat";
import "../styles/chat.css";

const newConvId = () => "conv-" + Math.random().toString(36).slice(2, 10);

/** 복원 스냅샷 정리 — va-restored 래퍼 해제(중첩 방지) + 시작 안내 문구 제거(dropWelcome)
 *  + 차트 박스 초기화(data-drawn 제거·비우기 — hydrate 가 다시 그린다, 바닐라 openSession) */
function flattenRestoredHtml(html: string): string {
  if (!html) return "";
  try {
    const doc = new DOMParser().parseFromString(
      `<div id="__va_r">${html}</div>`,
      "text/html",
    );
    const root = doc.getElementById("__va_r");
    if (!root) return html;
    root.querySelectorAll(".va-restored").forEach((w) => {
      w.replaceWith(...Array.from(w.childNodes));
    });
    root.querySelectorAll(".message.welcome").forEach((n) => n.remove());
    root.querySelectorAll(".message.agent").forEach((n) => {
      if ((n.textContent || "").includes("새 평가 대화를 시작합니다")) n.remove();
    });
    root.querySelectorAll(".chart-box[data-drawn]").forEach((b) => {
      b.removeAttribute("data-drawn");
      b.innerHTML = "";
      (b as HTMLElement).hidden = false;
    });
    return root.innerHTML;
  } catch {
    return html;
  }
}

/** 스냅샷 HTML 에서 screen_panel 스펙 복원 — 그 대화의 결과 세트 전체 (자동 오픈 없음) */
function screenSpecsFromHtml(html: string): ScreenSpec[] {
  if (!html) return [];
  const specs: ScreenSpec[] = [];
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll(".screen-panel-data").forEach((n) => {
      try {
        const s = JSON.parse(n.getAttribute("data-spec") || "null");
        if (s && Array.isArray(s.rows)) specs.push(s);
      } catch {
        /* 손상된 스펙은 건너뛴다 */
      }
    });
  } catch {
    /* noop */
  }
  return specs;
}

/** messagesHtml 없는(방어적) 스냅샷 — history 배열로 말풍선 재구성 */
function turnsFromHistory(history: HistMsg[]): ChatTurn[] {
  return history.map((m): ChatTurn => {
    if (m.role === "user") return { role: "user", time: "", text: m.content };
    const t = newAgentTurn();
    t.live = false;
    t.progress.finalized = true;
    t.flow = [{ kind: "text", html: mdLite(extractChoices(m.content).text) }];
    return t;
  });
}

/** 구버전 스냅샷의 traceGroups 복원 (rows 가 문자열 배열이던 시절 호환) */
function restoreTraceGroups(s: VaSession): TraceGroup[] {
  if (Array.isArray(s.traceGroups) && s.traceGroups.length) {
    return s.traceGroups.map((g) => ({
      ...g,
      open: false,
      rows: (g.rows || []).map((r) =>
        typeof r === "string" ? { t: r, status: "ok" as const } : { ...r },
      ),
    }));
  }
  return (s.traceSteps || []).map((t) => ({
    tool: t.tool,
    symbol: null,
    rows: [],
    done: true,
    ok: !!t.ok,
    open: false,
  }));
}

// 빠른 실행 ⚡ 기본 카테고리 (바닐라 QUICK_DEFAULT)
const QUICK_DEFAULT: Array<[string, string[]]> = [
  ["기업 조회", ["삼성전자 기본정보 보여줘", "삼성전자 공시·뉴스 알려줘"]],
  ["기업 발굴", ["매출 1조원 이상 기업 찾아줘", "양자컴퓨터 특허 보유 기업 찾아줘"]],
  ["투자 분석", ["삼성전자 최근 30일 주가에 영향 준 사건 정리해줘", "요즘 뜨는 산업 키워드 중 구조적 트렌드만 골라줘"]],
  ["가치평가", ["상대가치평가 하고 싶어", "WACC 계산 · 베타(β) 구하기"]],
  ["평가 샘플 · 엑셀 산출", ["DCF 샘플로 평가하고 엑셀까지 만들어줘", "RCPS · 스톡옵션 샘플 평가"]],
];

// 테마 순환 (바닐라 applyTheme — light → ivory → dark)
const THEME_ORDER = ["light", "ivory", "dark"] as const;
const THEME_LABEL: Record<string, string> = {
  light: "라이트",
  ivory: "아이보리",
  dark: "다크",
};

export default function ChatPage() {
  const { conversationId: urlConv } = useParams();
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const sessions = useChat((s) => s.sessions);
  const writingIds = useChat((s) => s.writingIds);
  const doneIds = useChat((s) => s.doneIds);
  const resetEpoch = useChat((s) => s.resetEpoch);

  // 예시 질문의 '분위기 안 좋은 종목' — 전일 하락 시총 상위에서 매일 바뀐다.
  // 실패하면 기본 종목으로 남고 카드는 그대로 뜬다.
  const [declinerName, setDeclinerName] = useState(DECLINER_FALLBACK);
  // 기업 발굴 카드 셔플 — 기본 세트 ↔ DS Query 검증 질문 세트 토글
  const [discoveryAlt, setDiscoveryAlt] = useState(false);
  // 첫화면 인사말 — 시간대별 세계 각국 인사를 랜덤으로. 마운트(새로고침)와
  // 빈 화면 복귀(새 채팅) 때마다 다시 고른다.
  const [greet, setGreet] = useState(() => pickGreeting());
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const r = await api<{ ok: boolean; data?: {
          items?: { name?: string }[]; pick?: { name?: string };
        } }>("/agent/market/decliners");
        // 목록에서 매번 새로 고른다 — 새 채팅·새로고침마다 다른 종목이 보여야 한다.
        // 서버가 고른 pick 은 목록이 비었을 때의 대비책이다(응답이 캐시돼도
        // 클라이언트가 고르므로 화면은 계속 바뀐다).
        const names = (r?.data?.items || []).map((x) => x?.name).filter(Boolean) as string[];
        const n = names.length
          ? names[Math.floor(Math.random() * names.length)]
          : r?.data?.pick?.name;
        if (alive && n) setDeclinerName(n);
      } catch {
        /* 기본 종목 유지 */
      }
    })();
    return () => { alive = false; };
  }, []);

  // ── 대화 상태 (렌더) ──
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [restoredHtml, setRestoredHtml] = useState("");
  const [title, setTitle] = useState("새 채팅");
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [showJump, setShowJump] = useState(false);
  const [theme, setTheme] = useState<string>(
    () => document.documentElement.getAttribute("data-theme") || "light",
  );
  // ── 3-B 상태 ──
  const [targets, setTargets] = useState<VaTarget[]>([]);
  const [activeTargetIdx, setActiveTargetIdxState] = useState(-1);
  const [traceGroups, setTraceGroups] = useState<TraceGroup[]>([]);
  const [pendingFiles, setPendingFilesState] = useState<File[]>([]);
  const [rpSpecs, setRpSpecs] = useState<ScreenSpec[]>([]);
  const [rpIdx, setRpIdx] = useState(-1);
  // 통합 우측 패널 — 탭 [스크리닝 결과 | 파일 미리보기]. 내용 유무는 rpSpecs/previewFile 파생.
  const [rightPanel, setRightPanel] = useState<RightPanelState>(RIGHT_PANEL_CLOSED);
  // 칩 위에 1초 이상 머무르면 그 질문을 입력창에 회색으로 예고한다.
  // 마우스가 칩을 떠나도 유지 — 그래야 컴포저로 옮겨 클릭할 수 있다.
  const [hoverQ, setHoverQ] = useState("");
  const hoverTimerRef = useRef<number | undefined>(undefined);
  const armHoverQ = (q: string) => {
    window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = window.setTimeout(() => setHoverQ(q), 1000);
  };
  const cancelHoverQ = () => window.clearTimeout(hoverTimerRef.current);

  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);
  // 업로드한 첨부의 이름→URL (서버가 스트림 첫 프레임 uploads 로 준다).
  // 대화 단위 상태 — 새 채팅·대화 전환 시 비운다. 칩의 클릭 가능 여부는 확장자로
  // 정해지고 URL 은 클릭 시점에 읽으므로 리렌더가 필요 없다 → state 아닌 ref.
  const uploadUrlsRef = useRef<Record<string, string>>({});
  const [panelOff, setPanelOff] = useState<boolean>(() => {
    try {
      return localStorage.getItem("va_panel_off") === "1";
    } catch {
      return false;
    }
  });
  const [libraryOpen, setLibraryOpen] = useState(false);
  // 문서함은 전용 URL(/library)을 가진다 — 열림 상태를 경로와 동기화
  const routerLoc = useLocation();
  useEffect(() => {
    const onLibraryRoute = routerLoc.pathname === "/library";
    if (onLibraryRoute && !libraryOpen) {
      saveCurrent();
      dispatchPanel({ type: "close" });
      setPreviewFile(null);
      setLibraryOpen(true);
    } else if (!onLibraryRoute && libraryOpen) {
      setLibraryOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLoc.pathname]);
  const [sideCollapsed, setSideCollapsed] = useState<boolean>(initialCollapsed);
  const [sideResizing, setSideResizing] = useState(false);
  const [rpResizing, setRpResizing] = useState(false);
  const [qnavItems, setQnavItems] = useState<Array<{ q: string; sum: string }>>([]);
  const [qnavActive, setQnavActive] = useState(-1);
  const [titleMenuAnchor, setTitleMenuAnchor] = useState<HTMLElement | null>(null);
  const [titleRenaming, setTitleRenaming] = useState(false);

  // ── 대화 상태 (ref — 이벤트 루프에서 직접 갱신) ──
  const turnsRef = useRef<ChatTurn[]>(turns);
  const convIdRef = useRef(newConvId());
  // busy = "지금 보고 있는 대화가 작성 중" — 전역 잠금이 아니라 대화별 (P1b).
  // writingIds(store)가 바뀌거나 대화 전환(setState 동반)이 일어나면 재계산된다.
  const busy = writingIds.includes(convIdRef.current);
  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);
  const activeRef = useRef<string | null>(null);
  const historyRef = useRef<HistMsg[]>([]);
  const targetsRef = useRef<VaTarget[]>([]);
  const activeTargetIdxRef = useRef(-1);
  const traceStepsRef = useRef<Array<{ tool: string; ok: boolean }>>([]);
  const traceGroupsRef = useRef<TraceGroup[]>([]);
  const traceCurrentRef = useRef(-1);
  const titleRef = useRef(title);
  const titleLockedRef = useRef(false);
  const modelTitledRef = useRef(false);
  const firstQuestionRef = useRef("");
  const busyRef = useRef(false);
  const pendingFilesRef = useRef<File[]>([]);
  // 답변 생성 중 들어온 질문 — 버리지 않고 완료 후 순서대로 보낸다 (철학 기본 4).
  // 대화 전환이 열리면서 큐도 대화별로 나뉜다 (P1b)
  const queuesRef = useRef<Record<string, { text: string; files: File[] }[]>>({});
  const [queuedMsgs, setQueuedMsgs] = useState<{ text: string; nFiles: number }[]>([]);
  const syncQueued = () =>
    setQueuedMsgs(
      (queuesRef.current[convIdRef.current] || []).map((q) => ({
        text: q.text,
        nFiles: q.files.length,
      })),
    );
  // 스트림 중단은 그 대화의 것만 — 전역 하나면 두 번째 전송이 첫 스트림의 중단을 뺏는다
  const abortMapRef = useRef<Record<string, AbortController>>({});
  const currentGroupRef = useRef<string | null>(null); // 새 채팅이 속할 그룹 (그룹 행 ✎)
  // 스트리밍 조각 (바닐라 ensureSeg/sealSeg/scheduleStreamRender)
  const rafRef = useRef(0);
  const lastRenderRef = useRef(0);
  const choicesOpenRef = useRef(false);
  const nextChipsRef = useRef<string[]>([]);
  // 스크롤 고정
  const scrollRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef(true);
  const touchYRef = useRef<number | null>(null);
  const aliveRef = useRef(true);

  const isEmpty = turns.length === 0 && !restoredHtml;
  const hasConv = !!activeSessionId;
  // Graph DB 챗(QC) 여부 — URL 또는 대화 ID 프리픽스 (복원된 dskg 대화 포함)
  const isGraphChat =
    routerLoc.pathname.startsWith("/graph-chat") || isGraphConv(convIdRef.current);

  // 대화하다가 빈 첫화면으로 돌아오면(새 채팅) 인사말을 다시 뽑는다
  const wasEmptyRef = useRef(isEmpty);
  useEffect(() => {
    if (isEmpty && !wasEmptyRef.current) setGreet(pickGreeting());
    wasEmptyRef.current = isEmpty;
  }, [isEmpty]);

  // ── 통합 우측 패널 파생값 — 표시 여부·실제 표시 탭 (내용 없는 탭은 있는 쪽 보정) ──
  const panelContent = { hasResults: rpSpecs.length > 0, hasPreview: !!previewFile };
  const panelVisible = isPanelVisible(rightPanel, panelContent);
  const panelTab = effectiveTab(rightPanel.tab, panelContent) ?? "results";
  const dispatchPanel = (ev: RightPanelEvent) => {
    setRightPanel((s) => rightPanelReduce(s, ev));
    window.setTimeout(() => VAChart.resize(), 60); // 채팅 폭이 변한다 — 차트 재계측
  };

  const setActiveTargetIdx = (i: number) => {
    activeTargetIdxRef.current = i;
    setActiveTargetIdxState(i);
  };
  const setPendingFiles = (files: File[]) => {
    pendingFilesRef.current = files;
    setPendingFilesState(files);
  };
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      Object.values(abortMapRef.current).forEach((a) => a.abort());
      VAChart.dispose(document); // 페이지 이탈 — 살아있는 차트 인스턴스 정리
    };
  }, []);

  // ── 차트 (3-B): 인증 fetcher 주입 + 위젯 followup(va:ask) → send ──
  useEffect(() => {
    VAChart.setFetcher(async (url: string) => {
      const r = await fetch(url, { headers: authHeaders() });
      if (!r.ok) throw new Error(gatewayMessage(r.status) || "HTTP " + r.status);
      return r.json();
    });
  }, []);
  const sendFnRef = useRef<(t: string) => Promise<void>>(async () => {});
  useEffect(() => {
    const h = (e: Event) => {
      const t = String((e as CustomEvent).detail?.text || "").trim();
      if (t && !busyRef.current) void sendFnRef.current(t);
    };
    document.addEventListener("va:ask", h);
    return () => document.removeEventListener("va:ask", h);
  }, []);
  // 렌더된 차트 자리표시자 hydrate — 텍스트 확정·아티팩트·복원 후마다
  useEffect(() => {
    if (listRef.current) VAChart.hydrate(listRef.current);
  }, [turns, restoredHtml]);

  // ── qnav — 유저 질문 틱 마크 (DOM 스캔: 복원 HTML 포함, 바닐라 renderQNav) ──
  useEffect(() => {
    const list = listRef.current;
    if (!list) {
      setQnavItems([]);
      return;
    }
    const arts = [...list.querySelectorAll("article.message.user")];
    setQnavItems(
      arts.map((art) => {
        const q = (art.querySelector(".message-text")?.textContent || "").trim();
        // 해당 질문 바로 다음 에이전트 답변의 첫 문단을 요약으로
        let n = art.nextElementSibling;
        while (n && !n.classList.contains("agent")) n = n.nextElementSibling;
        const sum = n
          ? (n.querySelector(".message-text")?.textContent || "").trim().slice(0, 160)
          : "";
        return { q, sum };
      }),
    );
  }, [turns, restoredHtml]);

  // 모바일 오버레이 접기 — LabShell 과 공용 훅 (lib/useMobileSide)
  useMobileSide(sideCollapsed, () => setSideCollapsed(true));
  const collapseSidebarOnMobile = () => {
    if (isMobile()) setSideCollapsed(true);
  };
  // 사이드바 접힘 애니메이션 — LabShell 과 공용 훅 (lib/useRailSettle)
  const { railSettled, sideAnimating } = useRailSettle(sideCollapsed);

  const setSideCollapsedPersist = (on: boolean) => {
    setSideCollapsed(on);
    try {
      localStorage.setItem("va_side_collapsed", on ? "1" : "0");
    } catch {
      /* noop */
    }
  };

  // ── 스토어 초기화·계정 전환·재동기화 ──
  useEffect(() => {
    useChat.getState().init(user);
  }, [user]);

  const epochRef = useRef(resetEpoch);
  useEffect(() => {
    if (resetEpoch !== epochRef.current) {
      // 실제 계정 전환 — 이전 계정 잔상 제거 (첫 인증은 리셋하지 않는다)
      epochRef.current = resetEpoch;
      resetToNew();
      navigate("/", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetEpoch]);

  useEffect(() => {
    const f = () => useChat.getState().maybeResync();
    document.addEventListener("visibilitychange", f);
    window.addEventListener("focus", f);
    return () => {
      document.removeEventListener("visibilitychange", f);
      window.removeEventListener("focus", f);
    };
  }, []);

  // ── 커밋: ref 배열 → setState (얕은 복사로 리렌더 트리거) ──
  const commit = () => {
    if (!aliveRef.current) return;
    setTurns([...turnsRef.current]);
    setTargets(targetsRef.current.slice());
    setTraceGroups(
      traceGroupsRef.current.map((g) => ({ ...g, rows: g.rows.map((r) => ({ ...r })) })),
    );
  };

  // ── 스크롤 고정 (sticky bottom) ──
  const scrollDown = useCallback((force?: boolean) => {
    if (force) stickRef.current = true;
    const el = scrollRef.current;
    if (!el) return;
    if (!stickRef.current) {
      setShowJump(true);
      return;
    }
    el.scrollTop = el.scrollHeight;
    setShowJump(false);
  }, []);
  const nearBottom = () => {
    const el = scrollRef.current;
    return !!el && el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  // ── 제목 ──
  const setTitleBoth = (t: string) => {
    const v = (t || "새 채팅").slice(0, 60);
    titleRef.current = v;
    if (aliveRef.current) setTitle(v);
  };

  // ── 진행 로그 (바닐라 setProgress/finalizeProgress) ──
  // 진행 로그는 반드시 '그 스트림의 턴'에 쓴다 — lastAgent() 로 찾으면
  // 대화를 전환했을 때 다른 대화의 턴을 밟는다 (P1b)
  const setProgress = (t: AgentTurn, kind: ProgressLine["kind"], msg: string) => {
    console.log("[DBG setProgress]", kind, msg.slice(0, 60), "hist=", t.progress.history.length);
    const p = t.progress;
    // 같은 내용이 시간만 바뀌어 반복되면(경과 표시 등) 현재 라인만 갱신
    if (kind === "spin" && p.current && progressKey(p.current.text) === progressKey(msg)) {
      p.current = { kind: "spin", text: msg };
      commit();
      return;
    }
    if (p.current) p.history.push(p.current); // 기존 현재 라인 → 이전 단계로
    p.current = { kind, text: msg };
    commit();
    scrollDown();
  };
  const finalizeProgress = (t: AgentTurn) => {
    const p = t.progress;
    // 접혀 있는 이전 단계도 완료형으로 — 내용 없는 라인은 버린다
    p.history = p.history
      .map((l) => ({ ...l, text: finishedPhrase(l.text) }))
      .filter((l) => l.text);
    if (p.current) {
      if (/^(다음 단계 판단|분석)\s*중/u.test(p.current.text)) {
        p.current = null;
      } else {
        const done = finishedPhrase(p.current.text);
        p.current = { ...p.current, text: done || p.current.text };
      }
    }
    p.finalized = true;
  };

  // ── 진행 내역(trace) — 우측 패널 + 스냅샷 호환 데이터 ──
  const activeTargetKey = (): string | null => {
    if (activeTargetIdxRef.current === -1) return null; // 전체
    const t = targetsRef.current[activeTargetIdxRef.current];
    return t ? t.ticker || t.name : null;
  };
  const traceStart = (tool: string) => {
    traceGroupsRef.current.push({
      tool,
      symbol: null,
      rows: [],
      done: false,
      ok: false,
      open: false,
    });
    traceCurrentRef.current = traceGroupsRef.current.length - 1;
  };
  const traceSub = (msg: string) => {
    const g = traceGroupsRef.current[traceCurrentRef.current];
    if (!g) return;
    const m = String(msg).slice(0, 120);
    const last = g.rows[g.rows.length - 1];
    if (last && last.status === "run" && progressKey(last.t) === progressKey(m)) {
      last.t = m;
    } else {
      if (last && last.status === "run") last.status = "ok"; // 앞 단계는 끝난 것으로
      g.rows.push({ t: m, status: "run" });
      if (g.rows.length > 20) g.rows.shift();
    }
  };
  const updateTrace = (tool: string, ok: boolean) => {
    traceStepsRef.current.push({ tool, ok });
    let g = traceGroupsRef.current[traceCurrentRef.current];
    if (!g || g.tool !== tool) {
      traceStart(tool);
      g = traceGroupsRef.current[traceCurrentRef.current];
    }
    g.done = true;
    g.ok = !!ok;
    for (let i = g.rows.length - 1; i >= 0; i--) {
      if (g.rows[i].status !== "run") break;
      g.rows[i].status = ok ? "ok" : "fail";
    }
    if (!g.symbol) g.symbol = activeTargetKey();
    // 같은 기업의 같은 작업이 이미 있으면 그 아래로 실행 내역을 쌓는다
    const cur = traceCurrentRef.current;
    const prev = traceGroupsRef.current.findIndex(
      (x, i) => i !== cur && x.tool === tool && x.symbol === g.symbol,
    );
    if (prev >= 0) {
      traceGroupsRef.current[prev].rows.push(...g.rows);
      traceGroupsRef.current[prev].done = true;
      traceGroupsRef.current[prev].ok = traceGroupsRef.current[prev].ok && !!ok;
      traceGroupsRef.current.splice(cur, 1);
    }
    traceCurrentRef.current = -1;
  };

  // ── 평가 대상 (company_card artifact — 우측 패널·제목·targets 폼 필드) ──
  const updateCompanyCard = (a: ChatArtifact) => {
    const raw = String(a.name || "").trim();
    if (!raw) return;
    const name = cleanCompanyName(raw);
    const ticker = a.ticker && a.ticker !== "—" ? String(a.ticker) : "";
    const list = targetsRef.current;
    let i = ticker ? list.findIndex((t) => t.ticker === ticker) : -1;
    if (i < 0) i = list.findIndex((t) => cleanCompanyName(t.name) === name);
    if (i >= 0) {
      if (ticker) list[i].ticker = ticker;
      if (name.length < list[i].name.length) list[i].name = name; // 더 짧은 표기 유지
    } else {
      list.push({ name, ticker });
      i = list.length - 1;
    }
    // 진행 중인 작업이 아직 어느 기업 것인지 모르면 이 카드의 기업으로 확정
    const g = traceGroupsRef.current[traceCurrentRef.current];
    if (g && !g.symbol) g.symbol = list[i].ticker || list[i].name;
  };
  const currentCompany = (): string | null => {
    const list = targetsRef.current;
    const t =
      activeTargetIdxRef.current === -1
        ? list[list.length - 1]
        : list[activeTargetIdxRef.current];
    return t ? t.name : null;
  };

  // ── 스트리밍 조각 (ensureSeg/sealSeg + rAF 배칭) ──
  // 조각 상태는 턴에 붙는다 — 대화별 동시 스트림에서 서로 밟지 않게 (P1b)
  const ensureSeg = (t: AgentTurn) => {
    if (t.segIdx !== null && t.segIdx !== undefined) return;
    t.flow.push({ kind: "text", html: "" });
    t.segIdx = t.flow.length - 1;
    t.buf = "";
  };
  const sealSeg = (t: AgentTurn) => {
    const i = t.segIdx;
    if (i !== null && i !== undefined) {
      const seg = t.flow[i];
      if (seg && seg.kind === "text" && !seg.html) t.flow.splice(i, 1); // 빈 조각 제거
    }
    t.segIdx = null;
  };
  const flushStream = (t: AgentTurn) => {
    lastRenderRef.current = performance.now();
    const i = t.segIdx;
    if (i === null || i === undefined) return;
    const seg = t.flow[i];
    if (seg && seg.kind === "text") {
      seg.html = mdLite(stripMarkers(t.buf || ""), { noCharts: true });
    }
    commit();
    scrollDown();
  };
  const STREAM_RENDER_MS = 120;
  const scheduleStreamRender = (t: AgentTurn) => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      if (performance.now() - lastRenderRef.current < STREAM_RENDER_MS) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = 0;
          flushStream(t);
        });
        return;
      }
      flushStream(t);
    });
  };
  const cancelStreamRender = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
  };

  // ── 꼬리 질문 수집 (renderNext) — 차트 followups + [[NEXT]] 를 한곳에 모아 중복 제거 ──
  const collectNext = (list: string[], t: AgentTurn, final: boolean) => {
    if (choicesOpenRef.current) return; // 되묻기 대기 중 — 꼬리 질문 없음
    for (const q of list || []) {
      const n = normalizeNextChip(q);
      if (n && !nextChipsRef.current.includes(n)) nextChipsRef.current.push(n);
    }
    if (final) {
      t.next = nextChipsRef.current.slice(0, 5);
      t.nextFinal = true;
    }
  };

  // ── 세션 저장 (바닐라 snapshot/saveCurrent) ──
  const sessionSubtitle = (): string => {
    const userMsgs = historyRef.current
      .filter((m) => m.role === "user")
      .map((m) => (m.content || "").trim())
      .filter(Boolean);
    const ct = currentCompany() || "";
    const sub =
      ct && ct !== "—" && ct !== (userMsgs[0] || "")
        ? "조회: " + ct
        : userMsgs.slice(1, 3).join(" · ");
    return (sub || "").slice(0, 60);
  };
  const saveCurrent = useCallback(() => {
    if (!historyRef.current.some((m) => m.role === "user")) return; // 빈 대화는 저장 안 함
    const toolCount = new Set(traceGroupsRef.current.map((g) => g.tool)).size;
    useChat.getState().saveSession({
      id: convIdRef.current,
      title: (titleRef.current || "").trim() || "새 대화",
      titleLocked: titleLockedRef.current,
      group: currentGroupRef.current || undefined, // 채팅 그룹 — 서버 스냅샷으로 동기화
      sub: sessionSubtitle(),
      ts: nowTime(),
      tsMs: Date.now(),
      // 바닐라 openSession 이 그대로 innerHTML 하는 필드 — React 렌더 결과가
      // 바닐라와 같은 클래스 구조라 양방향 호환된다
      messagesHtml: listRef.current ? listRef.current.innerHTML : "",
      history: historyRef.current.slice(),
      traceSteps: traceStepsRef.current.slice(),
      traceGroups: traceGroupsRef.current.map((g) => ({
        ...g,
        rows: g.rows.map((r) => ({ ...r })),
      })),
      targets: targetsRef.current.slice(),
      activeTargetIdx: activeTargetIdxRef.current,
      userPickedTarget: activeTargetIdxRef.current !== -1,
      traceTitle: toolCount
        ? `${toolCount}개 작업 진행.`
        : "아직 진행한 작업이 없습니다.",
      traceKv: "", // 바닐라는 traceGroups 가 있으면 이 필드를 쓰지 않는다
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /** 백그라운드 완료용 스냅샷 — DOM 이 다른 대화를 보여주고 있어 innerHTML 을
   *  쓸 수 없다. 턴 객체(flow 조각에 최종 html 이 있음)를 MessageList 와 같은
   *  클래스 구조로 직렬화한다. 아티팩트(다운로드·결과 패널 spec 포함)도 살아난다. */
  const esc = (v: string) =>
    v.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const turnsToSnapshotHtml = (list: ChatTurn[]): string =>
    list
      .map((t) => {
        if (t.role === "user") {
          return (
            `<article class="message user"><div class="message-role">USER</div><div>` +
            `<div class="message-head"><span class="message-time">${esc(t.time || "")}</span></div>` +
            `<div class="message-text">${esc(t.text || "")}</div></div></article>`
          );
        }
        const flow = t.flow
          .map((f) => {
            if (f.kind === "text") return `<div class="message-text">${f.html}</div>`;
            if (f.kind === "art") {
              const attrs = Object.entries(f.attrs || {})
                .map(([k, v]) => ` ${k}="${esc(v)}"`)
                .join("");
              return `<div class="${esc(f.className || "art")}"${attrs}>${f.html}</div>`;
            }
            return "";
          })
          .join("");
        const arts = (t.downloads || [])
          .map((d) => `<div class="art"${d.quiet ? ' style="display:none"' : ""}>${d.html}</div>`)
          .join("");
        // 진행 단계('이전 단계 보기') — MessageList 와 같은 클래스라 복원 후에도
        // 위임 핸들러(.progress-toggle)가 접기/펼치기를 처리한다
        const pr = t.progress;
        const lead = (k: string) => (k === "ok" ? "✓ " : k === "tool" ? "▸ " : "· ");
        const lineCls = (k: string) =>
          "progress-line" + (k === "tool" ? " tool" : k === "ok" ? " done-ok" : "");
        let progress = "";
        if (pr && (pr.history.length || pr.current)) {
          const hist = pr.history
            .map((l) => `<div class="${lineCls(l.kind)}">${lead(l.kind)}<span>${esc(l.text)}</span></div>`)
            .join("");
          progress =
            `<div class="progress-log" data-log>` +
            (pr.history.length
              ? `<button class="progress-toggle" type="button">이전 단계 보기 (${pr.history.length}개) ▾</button>` +
                `<div class="progress-history" hidden>${hist}</div>`
              : "") +
            (pr.current ? `<div class="progress-line done-ok"><span>${esc(pr.current.text)}</span></div>` : "") +
            `</div>`;
        }
        return (
          `<article class="message agent"><div class="message-role">AGENT</div><div>` +
          `<div class="message-head"><span class="message-name">Valuation Agent</span>` +
          `<span class="message-time">${esc(t.time || "")}</span></div>` +
          progress +
          `<div class="message-flow" data-flow>${flow}</div><div data-arts>${arts}</div></div></article>`
        );
      })
      .join("");

  const saveCapturedSession = (
    cid: string,
    list: ChatTurn[],
    hist: HistMsg[],
    groups: TraceGroup[],
    bgTitle: string | null,
  ) => {
    const st = useChat.getState();
    const prev = st.sessions.find((x) => x.id === cid);
    const toolCount = new Set(groups.map((g) => g.tool)).size;
    const firstUser = hist.find((m) => m.role === "user")?.content || "";
    st.saveSession({
      id: cid,
      title: bgTitle || prev?.title || firstUser.slice(0, 60) || "새 대화",
      titleLocked: prev?.titleLocked ?? false,
      group: prev?.group,
      sub: prev?.sub || "",
      ts: nowTime(),
      tsMs: Date.now(),
      messagesHtml: turnsToSnapshotHtml(list),
      history: hist.slice(),
      traceSteps: prev?.traceSteps ? prev.traceSteps.slice() : [],
      traceGroups: groups.map((g) => ({ ...g, rows: g.rows.map((r) => ({ ...r })) })),
      targets: prev?.targets ? prev.targets.slice() : [],
      activeTargetIdx: -1,
      userPickedTarget: false,
      traceTitle: toolCount ? `${toolCount}개 작업 진행.` : "아직 진행한 작업이 없습니다.",
      traceKv: "",
    });
  };

  /** 커밋된 DOM 이 반영된 뒤 innerHTML 을 캡처해 저장 */
  const saveCurrentAfterRender = () => {
    window.setTimeout(() => {
      if (aliveRef.current) saveCurrent();
    }, 0);
  };

  // ── 새 대화 / 대화 열기 (바닐라 resetToNew/openSession) ──
  const resetToNew = () => {
    convIdRef.current = newConvId();
    activeRef.current = null;
    setActiveSessionId(null);
    historyRef.current = [];
    traceStepsRef.current = [];
    traceGroupsRef.current = [];
    traceCurrentRef.current = -1;
    targetsRef.current = [];
    firstQuestionRef.current = "";
    modelTitledRef.current = false;
    titleLockedRef.current = false;
    currentGroupRef.current = null;
    nextChipsRef.current = [];
    choicesOpenRef.current = false;
    turnsRef.current = [];
    setTurns([]);
    setRestoredHtml("");
    setTitleBoth("새 채팅");
    setTargets([]);
    setActiveTargetIdx(-1);
    setTraceGroups([]);
    setPendingFiles([]);
    uploadUrlsRef.current = {};   // 업로드 URL 은 대화 단위 (바닐라 dpOpened 와 동일)
    setRpSpecs([]);
    setRpIdx(-1);
    setRightPanel(RIGHT_PANEL_CLOSED);
    setPreviewFile(null);
    setLibraryOpen(false);
    setTitleRenaming(false);
    stickRef.current = true;
    setShowJump(false);
    syncQueued();
  };

  const openSessionRef = useRef<(s: VaSession) => void>(() => {});
  // 첫 메시지의 navigate(/c/:id)가 라우터에 반영되기 전, sessions 갱신으로
  // urlConv 효과가 먼저 돌면 'urlConv 없음 + history 있음'이 뒤로가기로 오인돼
  // resetToNew→openSession(스냅샷)이 연쇄된다 — 첫 턴 진행 단계가 통째로
  // 사라지던 버그. navigate 를 예약해 두고 반영될 때까지 그 분기를 보류한다.
  const pendingNavRef = useRef<string | null>(null);
  const openSession = (s: VaSession) => {
    useChat.getState().clearDone(s.id); // 열어 봤으니 완료 알림 해제
    setLibraryOpen(false); // 문서함에서 대화를 열면 채팅으로 복귀 (바닐라 closeLibrary)
    saveCurrent(); // 기존 대화 저장
    convIdRef.current = s.id;
    activeRef.current = s.id;
    setActiveSessionId(s.id);
    titleLockedRef.current = !!s.titleLocked;
    modelTitledRef.current = true; // 복원된 대화의 제목은 이미 확정
    currentGroupRef.current = s.group || null;
    historyRef.current = (s.history || []).slice();
    traceStepsRef.current = (s.traceSteps || []).slice();
    traceGroupsRef.current = restoreTraceGroups(s);
    traceCurrentRef.current = -1;
    targetsRef.current = Array.isArray(s.targets) ? s.targets.slice() : [];
    // 사용자가 직접 고른 필터만 복원 — 기본은 전체(-1). 구버전 스냅샷도 전체로 연다.
    const savedIdx = typeof s.activeTargetIdx === "number" ? s.activeTargetIdx : -1;
    setActiveTargetIdx(
      s.userPickedTarget && savedIdx >= 0 && savedIdx < targetsRef.current.length
        ? savedIdx
        : -1,
    );
    firstQuestionRef.current =
      (s.history || []).find((m) => m.role === "user")?.content || "";
    nextChipsRef.current = [];
    choicesOpenRef.current = false;
    setTitleBoth(s.title);
    setTitleRenaming(false);
    setTargets(targetsRef.current.slice());
    setTraceGroups(
      traceGroupsRef.current.map((g) => ({ ...g, rows: g.rows.map((r) => ({ ...r })) })),
    );
    // 복원: messagesHtml 우선(진행로그·아티팩트·칩 보존, 위임 클릭으로 동작),
    // 없으면 history 재렌더 폴백
    const html = flattenRestoredHtml(s.messagesHtml || "");
    turnsRef.current = html ? [] : turnsFromHistory(historyRef.current);
    setTurns(turnsRef.current.slice());
    setRestoredHtml(html);
    // 결과 패널 — 그 대화의 스크리닝 결과 세트 전체 (자동 오픈은 안 함)
    const specs = screenSpecsFromHtml(s.messagesHtml || "");
    setRpSpecs(specs);
    setRpIdx(specs.length - 1);
    setRightPanel(RIGHT_PANEL_CLOSED);
    setPreviewFile(null); // 미리보기는 대화 단위 상태가 아니다 — 전환 시 닫는다
    uploadUrlsRef.current = {};   // 지난 대화의 업로드 토큰은 이 대화에서 유효하지 않다
    collapseSidebarOnMobile();
    stickRef.current = true;
    window.setTimeout(() => scrollDown(true), 0);
    // 이 대화에 보류된 대기열 복원 + (작성 중이 아니면) 이어서 전송
    syncQueued();
    window.setTimeout(() => {
      const q = queuesRef.current[s.id];
      if (
        q?.length &&
        convIdRef.current === s.id &&
        !useChat.getState().writingIds.includes(s.id)
      ) {
        const nxt = q.shift()!;
        syncQueued();
        setPendingFiles(nxt.files);
        void sendFnRef.current(nxt.text);
      }
    }, 0);
  };
  openSessionRef.current = openSession;

  // ── 딥링크 /c/:id — 캐시(세션 목록)→서버 순으로 시도 (바닐라 tryOpenUrlConv) ──
  useEffect(() => {
    if (urlConv) {
      if (pendingNavRef.current === urlConv) pendingNavRef.current = null; // 예약 도착
      if (urlConv === convIdRef.current) return; // 이미 표시 중 (첫 메시지 replace 등)
      const s = sessions.find((x) => x.id === urlConv);
      if (s) openSession(s);
      // 목록에 없으면 refreshFromServer 가 sessions 를 채울 때 이 효과가 재시도한다
    } else if (pendingNavRef.current) {
      // 첫 메시지 navigate 가 아직 라우터에 반영되기 전 — 뒤로가기가 아니다
      return;
    } else if (routerLoc.pathname === "/library") {
      // 문서함도 '대화 id 없는 URL'이라 아래 리셋에 걸렸다 — 채팅을 보다가
      // 문서함을 누르면 새 채팅이 열리는 원인(Bishop 제보). 문서함 전환은
      // 경로 동기화 effect 가 처리하고, 보던 대화는 그대로 둔다(닫으면 복귀).
      return;
    } else if (activeRef.current || historyRef.current.length) {
      // 뒤로가기 등으로 / 복귀 → 새 대화 (바닐라 popstate)
      saveCurrent();
      resetToNew();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlConv, sessions]);

  // ── Graph DB 챗(QC) 모드 — /graph-chat 진입 시 대화 ID 에 dskg: 프리픽스를
  // 붙여 모드를 고정한다 (저장·복원·후속 턴 모두 프리픽스로 판정). 떠나면
  // 일반 새 대화로 리셋해 dskg 모드가 일반 챗에 잔존하지 않게 한다. ──
  useEffect(() => {
    const onGraph = routerLoc.pathname.startsWith("/graph-chat");
    const isG = isGraphConv(convIdRef.current);
    if (onGraph && !isG) {
      if (activeRef.current || historyRef.current.length) saveCurrent();
      resetToNew();
      convIdRef.current = GRAPH_CONV_PREFIX + convIdRef.current;
    } else if (!onGraph && isG && !urlConv && routerLoc.pathname !== "/library") {
      if (activeRef.current || historyRef.current.length) saveCurrent();
      resetToNew();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerLoc.pathname]);

  // ── screen_panel 등록 (바닐라 registerScreenPanel — 최신이 기본, 자동 오픈) ──
  const registerScreenPanel = (a: ChatArtifact) => {
    if (!a || !Array.isArray(a.rows)) return;
    setRpSpecs((prev) => {
      const next = [...prev, a as ScreenSpec];
      setRpIdx(next.length - 1);
      return next;
    });
    dispatchPanel({ type: "results-arrived" }); // 자동 오픈 + 결과 탭 활성
  };

  // ── 파일 미리보기 (아티팩트 '미리보기' 버튼·문서함 '보기'·엑셀 바로 보기) ──
  // 문서함은 닫지 않는다 — 문서를 훑으며 옆 패널로 미리보는 흐름 (사용자 피드백:
  // 닫으면 뒤의 빈 새 채팅이 드러나 '새 채팅이 생긴 것'처럼 보였다)
  const openPreview = (f: PreviewFile) => {
    if (!f || !f.url) return;
    setPreviewFile(f);
    dispatchPanel({ type: "preview-opened" }); // 오픈 + 미리보기 탭 활성
  };
  const closePreview = () => {
    setPreviewFile(null);
    // 결과가 남아 있으면 결과 탭으로, 없으면 패널이 닫힌다
    dispatchPanel({ type: "preview-closed", hasResults: rpSpecs.length > 0 });
  };

  // ── SSE 이벤트 분기 (바닐라 handleEvent) ──
  const handleEvent = (
    ev: SseEvent,
    t: AgentTurn,
    setFinal: (s: string) => void,
    onView: () => boolean,
    bgTitle: { title: string | null },
  ) => {
    switch (ev.event) {
      case "status":
        setProgress(t, "spin", String(ev["message"] || ""));
        if (onView()) traceSub(String(ev["message"] || ""));
        break;
      case "tool_start": {
        const toolName = String(ev["tool"] || "");
        setProgress(t, "tool", toolLabel(toolName));
        // 호출 서명 — 어떤 도구를 어떤 인자로 불렀는지 (DeepSearch API 사용 감사용).
        // 접힌 '이전 단계 보기'와 우측 진행 내역 양쪽에 남는다.
        const sig = formatToolCall(toolName, ev["input"]);
        if (sig) setProgress(t, "plain", sig);
        if (onView()) {
          traceStart(toolName);
          if (sig) traceSub(sig);
        }
        break;
      }
      case "tool_end": {
        const ok = !!ev["ok"];
        setProgress(
          t,
          ok ? "ok" : "plain",
          `${toolLabel(String(ev["tool"] || ""))} ${ok ? "완료" : "실패"}`,
        );
        if (onView()) updateTrace(String(ev["tool"] || ""), ok);
        break;
      }
      case "artifact": {
        const a = (ev["artifact"] || {}) as ChatArtifact;
        if (a.type === "company_card") {
          if (onView()) {
            updateCompanyCard(a);
            commit(); // 평가 대상 카드 즉시 반영
          }
          break;
        }
        if (a.type === "screen_panel" && onView()) registerScreenPanel(a);
        const r = renderArtifactHtml(a);
        if (r) {
          if (a.type === "download") {
            // 다운로드 버튼은 답변 마지막(하단)에 — 텍스트가 이어져도 밀리지 않게
            t.downloads.push({ html: r.html, quiet: r.quiet });
          } else {
            sealSeg(t); // 다음 텍스트는 이 아티팩트 아래에서 시작
            t.flow.push({ kind: "art", className: r.className, html: r.html, attrs: r.attrs });
          }
          if (onView()) {
            commit();
            scrollDown();
          }
        }
        // 차트의 꼬리 질문은 위젯 안이 아니라 메시지 하단 한 곳에 모은다
        const fu = a.spec && a.spec.followups;
        if (fu && fu.length) collectNext(fu, t, false);
        break;
      }
      case "token": {
        if (!t.streaming) {
          t.streaming = true; // 스피너 제거 (바닐라 첫 token)
          if (onView()) useChat.getState().setStreaming(true);
        }
        ensureSeg(t);
        t.buf = (t.buf || "") + String(ev["text"] || "");
        if (onView()) scheduleStreamRender(t); // 화면에 있는 대화만 프레임 렌더
        break;
      }
      case "reply": {
        cancelStreamRender();
        const parsed = extractChoices(String(ev["text"] || ""));
        // 모델이 지은 제목([[TITLE]]) — 다른 대화를 보는 중이면 저장 때 쓰도록 보관만
        if (parsed.title) {
          if (onView() && !titleLockedRef.current) {
            modelTitledRef.current = true;
            setTitleBoth(parsed.title);
          } else if (!onView()) {
            bgTitle.title = parsed.title;
          }
        }
        ensureSeg(t);
        const i = t.segIdx!;
        const seg = t.flow[i];
        if (seg && seg.kind === "text") seg.html = mdLite(parsed.text);
        sealSeg(t); // 다음 스텝의 텍스트는 새 조각으로
        t.streaming = false;
        if (onView()) useChat.getState().setStreaming(false);
        if (parsed.choices && parsed.choices.length) {
          // 되묻기 — 꼬리 질문은 치운다 (고를 것이 두 종류면 헷갈린다)
          if (onView()) choicesOpenRef.current = true;
          t.choices = parsed.choices;
          t.next = [];
          t.nextFinal = false;
        } else {
          t.choices = null;
        }
        // 꼬리 질문은 답변 완료 시점에만 — 스트리밍 중 모인 차트 followups 도 이때 함께
        if (!ev["interim"]) {
          collectNext(parsed.next || [], t, true);
          setFinal(parsed.text);
        }
        if (onView()) {
          commit();
          scrollDown();
        }
        break;
      }
      case "retry":
        // 서버가 끊긴 스트림을 다시 시도 중 — 쓰다 만 조각을 비우고 새로 받는다
        t.buf = "";
        if (t.segIdx !== null && t.segIdx !== undefined) {
          const seg = t.flow[t.segIdx];
          if (seg && seg.kind === "text") seg.html = "";
        }
        setProgress(t, "spin", "연결이 불안정해 다시 시도하는 중…");
        break;
      // 업로드한 표 파일의 미리보기 URL — 스트림 첫 프레임으로 온다.
      // 첨부 칩을 클릭 가능하게 만들고, 첫 파일은 바로 우측 패널에 띄운다
      // (바닐라와 동일 — 무엇을 올렸는지 즉시 보여주는 게 질문보다 먼저다).
      case "uploads": {
        const ups = (ev["files"] as { name?: string; url?: string }[] | undefined) || [];
        const add: Record<string, string> = {};
        for (const f of ups) if (f?.name && f?.url) add[f.name] = f.url;
        if (!Object.keys(add).length) break;
        if (!onView()) break; // 업로드 토큰·미리보기는 그 대화 화면 전용
        uploadUrlsRef.current = { ...uploadUrlsRef.current, ...add };
        const first = ups.find((f) => f?.name && f?.url);
        if (first) openPreview({ url: first.url!, name: first.name!, kind: "excel" });
        break;
      }
      case "error":
        setProgress(t, "plain", "오류: " + String(ev["message"] || ""));
        break;
      case "heartbeat":
        break; // SSE keepalive — 무시
      case "done":
        finalizeProgress(t);
        if (onView()) commit();
        break;
    }
  };

  // ── 전송 (바닐라 send) ──
  const send = async (text: string) => {
    if (useChat.getState().writingIds.includes(convIdRef.current)) {
      // 이 대화가 생성 중 = 대기열로. 첨부도 그 질문에 실어 보관한다.
      const files = pendingFilesRef.current.slice();
      setPendingFiles([]);
      (queuesRef.current[convIdRef.current] ||= []).push({ text, files });
      syncQueued();
      return;
    }
    // 이전 답변의 꼬리 질문 칩은 걷어낸다 — 새 답변 생성 중 혼동 방지
    turnsRef.current.forEach((t) => {
      if (t.role === "agent") {
        t.next = [];
        t.nextFinal = false;
        t.choices = null;
      }
    });
    listRef.current
      ?.querySelectorAll(".va-restored .next-box, .va-restored .choice-box")
      .forEach((n) => n.remove());
    nextChipsRef.current = [];
    choicesOpenRef.current = false;

    const files = pendingFilesRef.current.slice();
    setPendingFiles([]); // 첨부는 이 질문에 실어 보낸다 (바닐라 clearFiles)

    turnsRef.current.push({
      role: "user",
      time: nowTime(),
      text,
      files: files.length ? files.map((f) => f.name) : undefined,
    });
    historyRef.current.push({ role: "user", content: text });
    if (!activeRef.current) {
      // 첫 메시지 → 이 대화의 고유 URL (바닐라 setChatUrl replace)
      pendingNavRef.current = convIdRef.current;
      navigate(`/c/${encodeURIComponent(convIdRef.current)}`, { replace: true });
    }
    const isFirst = historyRef.current.filter((m) => m.role === "user").length === 1;
    if (isFirst) {
      firstQuestionRef.current = text;
      if (!titleLockedRef.current)
        setTitleBoth(summarizeTitle(text, targetsRef.current));
    }
    // ── 이 스트림이 속한 대화를 캡처한다 (P1b) — 사용자가 다른 대화로 가도
    //    스트림은 이 객체들에만 쓴다. 화면 반영은 onView() 일 때만.
    const cid = convIdRef.current;
    const myTurns = turnsRef.current;
    const myHistory = historyRef.current;
    const myTraceGroups = traceGroupsRef.current;
    const bg: { title: string | null } = { title: null };
    const onView = () => convIdRef.current === cid;
    useChat.getState().addWriting(cid);

    const agent = newAgentTurn();
    myTurns.push(agent);
    commit();
    scrollDown(true); // 내가 보낸 질문은 바로 보여야 한다
    saveCurrentAfterRender(); // 질문은 보낸 순간 저장 — 생성 중 새로고침에도 유실 없음
    setProgress(agent, "spin", "분석 중…");

    let finalText = "";
    let endedEarly = "";   // "aborted" | "stalled" | "dropped" — finally 의 이력 기록에 사용
    const ac = new AbortController();
    abortMapRef.current[cid] = ac;
    const fd = new FormData();
    fd.append("messages", JSON.stringify(myHistory));
    fd.append("conversation_id", convIdRef.current);
    if (isGraphConv(convIdRef.current)) fd.append("mode", "dskg");
    // 평가 대상 — 모델이 '그 회사' 같은 지시어를 추측하지 않게
    if (targetsRef.current.length)
      fd.append("targets", JSON.stringify(targetsRef.current.slice(-8)));
    files.forEach((f) => fd.append("files", f, f.name));

    try {
      // 워치독(60s)은 postSse 내장 — 조용한 연결은 "stalled" 사유로 끊는다
      for await (const ev of postSse("/agent/chat", fd, {
        signal: ac.signal,
      })) {
        handleEvent(ev, agent, (s) => {
          finalText = s;
        }, onView, bg);
      }
    } catch (e) {
      const reason =
        e === "stalled" || (e instanceof Error && e.message === "stalled")
          ? "stalled"
          : e instanceof DOMException && e.name === "AbortError"
            ? "aborted"
            : e instanceof Error && e.message === "unauthorized"
              ? "unauthorized"
              : "error";
      endedEarly = reason === "error" ? "dropped" : reason;
      if (reason === "stalled") {
        setProgress(agent, "plain", "응답이 60초 이상 끊겨 중단했습니다. 다시 시도해 주세요.");
      } else if (reason === "aborted") {
        setProgress(agent, "plain", "· 중단됨");
      } else if (reason === "unauthorized") {
        setProgress(agent, "plain", "세션이 만료되었습니다. 다시 로그인하세요.");
        void useAuth.getState().checkAuth(); // 로그인 오버레이로 유도
      } else {
        // 스트림이 중간에 끊긴 경우(fetch 실패)는 원인이 대부분 서버 재배포·네트워크
        // 불안정이다. 원문("network error")은 사용자가 할 수 있는 게 없으니
        // 무엇을 하면 되는지로 바꿔 안내한다.
        const raw = e instanceof Error ? e.message : String(e);
        const dropped = /network error|failed to fetch|load failed|connection|ERR_/i.test(raw);
        setProgress(
          agent,
          "plain",
          dropped
            ? "서버 연결이 끊겼습니다 — 배포 중이거나 네트워크가 불안정할 수 있습니다. 같은 질문을 다시 보내주세요."
            : "오류: " + raw,
        );
      }
    } finally {
      delete abortMapRef.current[cid];
      if (onView()) cancelStreamRender();
      finalizeProgress(agent);
      // 끝나지 않은 작업 그룹을 완료로 표시 — 이 스트림의 것만 (캡처본)
      myTraceGroups.forEach((g) => {
        if (!g.done) {
          g.done = true;
          g.ok = true;
        }
      });
      // 완료 시각 옆에 총 소요를 남긴다 — "왜 오래 걸렸지?"를 나중에도 볼 수 있게
      agent.time = agent.startedAt
        ? `${nowTime()} · ${fmtElapsed(Date.now() - agent.startedAt)} 소요`
        : nowTime();
      agent.live = false;
      agent.streaming = false;
      agent.segIdx = null;
      if (finalText) {
        myHistory.push({ role: "assistant", content: finalText });
      } else if (endedEarly && endedEarly !== "unauthorized") {
        // 중단·끊김으로 답이 없으면 표식을 남긴다. 부분 계획을 그대로 넣으면
        // 모델이 사용자가 멈춘 그 계획을 이어가려 하므로 표식만 남긴다.
        myHistory.push({
          role: "assistant",
          content:
            endedEarly === "aborted"
              ? "[이전 응답은 사용자가 중단했습니다. 이어서 진행하지 말고 다음 지시를 따르세요.]"
              : "[이전 응답은 연결이 끊겨 완료되지 못했습니다.]",
        });
      }
      useChat.getState().removeWriting(cid);
      // 보고 있지 않은 대화가 끝났을 때만 완료 표시 — 눈앞에서 끝난 건 이미 봤다
      if (!onView()) useChat.getState().markDone(cid);

      if (onView() && turnsRef.current === myTurns) {
        // 내내 이 대화를 보고 있던 경우 — 기존 마무리 그대로
        useChat.getState().setStreaming(false);
        if (
          firstQuestionRef.current &&
          !titleLockedRef.current &&
          !modelTitledRef.current
        ) {
          setTitleBoth(summarizeTitle(firstQuestionRef.current, targetsRef.current));
        }
        activeRef.current = cid;
        if (aliveRef.current) setActiveSessionId(cid);
        commit();
        scrollDown();
        saveCurrentAfterRender();
      } else {
        // 떠나 있는 동안 완료 — 캡처본으로 저장한다. 화면은 건드리지 않는다.
        saveCapturedSession(cid, myTurns, myHistory, myTraceGroups, bg.title);
        if (onView() && aliveRef.current) {
          // 떠났다가 지금 막 돌아와 보고 있는 경우 — 방금 저장본으로 다시 연다
          const fresh = useChat.getState().sessions.find((x) => x.id === cid);
          if (fresh) window.setTimeout(() => openSessionRef.current(fresh), 0);
        }
      }
      // 대기열 — 자연 완료 + 그 대화를 보고 있을 때만 자동 전송.
      // (다른 대화를 보는 중이면 보류 — 그 대화를 다시 열 때 이어서 보낸다)
      if (!endedEarly && aliveRef.current && onView() && turnsRef.current === myTurns) {
        const nxt = (queuesRef.current[cid] || []).shift();
        if (nxt) {
          syncQueued();
          setPendingFiles(nxt.files);
          setTimeout(() => void sendFnRef.current(nxt.text), 0);
        }
      }
    }
  };
  sendFnRef.current = send;

  // 다른 화면에서 넘긴 질문 — 시장 이슈의 "이 이슈로 분석하기" 처럼 페이지를
  // 떠나며 남긴 프롬프트를 새 채팅에서 자동 전송한다. va:ask 이벤트는 ChatPage
  // 가 이미 떠 있을 때만 잡히므로, 라우팅을 건너온 경우는 세션 스토리지로 받는다.
  useEffect(() => {
    let text = "";
    try {
      text = (sessionStorage.getItem("va_pending_ask") || "").trim();
      if (text) sessionStorage.removeItem("va_pending_ask");
    } catch {
      /* noop */
    }
    if (text) void sendFnRef.current(text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 대기열 조작 — 칩 클릭=지금 보내기(유휴일 때), ×=빼기
  const sendQueuedNow = (i: number) => {
    if (useChat.getState().writingIds.includes(convIdRef.current)) return;
    const q = queuesRef.current[convIdRef.current] || [];
    const [nxt] = q.splice(i, 1);
    if (!nxt) return;
    syncQueued();
    setPendingFiles(nxt.files);
    void sendFnRef.current(nxt.text);
  };
  const removeQueued = (i: number) => {
    (queuesRef.current[convIdRef.current] || []).splice(i, 1);
    syncQueued();
  };

  const stop = () => {
    setHoverQ("");   // 중단 시 예고 질문도 비운다 — 전송 버튼 오클릭으로 재실행되지 않게
    abortMapRef.current[convIdRef.current]?.abort();
  };

  // ── 빠른 실행 ⚡ 내용 (바닐라 quickGroupsFor) — 열 때마다 계산 ──
  const getQuickGroups = (): Array<[string, string[]]> => {
    const company = currentCompany();
    const userMsgs = historyRef.current
      .filter((m) => m.role === "user")
      .map((m) => (m.content || "").trim())
      .filter(Boolean);
    const inConversation = userMsgs.length > 0;
    if (!inConversation) {
      if (!company) return QUICK_DEFAULT;
      const c = company;
      return [
        [
          `${c} 더 알아보기`,
          [`${c} 재무 데이터(매출·영업이익) 보여줘`, `${c} 공시·뉴스 알려줘`, `${c} 주주 현황 알려줘`],
        ],
        [
          `${c} 평가하기`,
          [`${c} 상대가치평가 해줘`, `${c} WACC 계산해줘 · 베타(β) 구하기`, `워런 버핏이 ${c}를 보면 뭐라고 할까?`],
        ],
        ["더 넓게", [`${c} 동종업계 peer 골라줘`, `${c} 주가 추이 엑셀로 만들어줘`]],
      ];
    }
    // 어미 변형("…평가해줘" vs "…평가해줄까")이 중복으로 들어오지 않게 정규화 키로 비교
    const canon = (t: string) =>
      String(t || "")
        .replace(/\s+/g, "")
        .replace(/(해\s*줘|해\s*줄까|해\s*볼까요?|줘|줄까|볼까요?|주세요|할까요?)[.?!]?$/, "");
    const asked = new Set(userMsgs.map(canon));
    const pri: string[] = [];
    const seen = new Set<string>();
    const add = (q: string) => {
      const t = String(q || "").trim();
      const k = canon(t);
      if (!t || pri.length >= 5 || !k || asked.has(k) || seen.has(k)) return;
      if (/DCF/i.test(t)) return; // DCF 는 전용 워크스페이스로 유도 — 연관 질문에서 제외
      seen.add(k);
      pri.push(t);
    };
    // ① 마지막 next-box 의 꼬리 질문 (세션 복원 후에도 DOM 에서 회수)
    const list = listRef.current;
    if (list) {
      const lastBox = list.querySelector(".message:last-of-type .next-box");
      const chips = lastBox
        ? lastBox.querySelectorAll(".next-chip")
        : list.querySelectorAll(".next-box .next-chip");
      chips.forEach((b) => add(b.textContent || ""));
    }
    // ② 기업 연관
    if (company) {
      const c = company;
      [
        `${c} 재무 데이터(매출·영업이익) 보여줘`, `${c} 공시·뉴스 알려줘`, `${c} 주주 현황 알려줘`,
        `${c} 동종업계 peer 골라줘`, `${c} 상대가치평가 해줘`, `워런 버핏이 ${c}를 보면 뭐라고 할까?`,
        // 기업조사 리포트는 칩으로 권하지 않는다 — 수 분 걸리는 5관점 심층이라
        // 무심코 눌리면 대화가 길게 막힌다. 명시적으로 요청할 때만 수행한다.
        `${c} 기업 리포트 만들어줘`,
      ].forEach(add);
    }
    // ③ 범용 보충
    [
      "매출 1조원 이상 기업 찾아줘",
      "부산 소재 매출액 1,000억 이상 회사 찾아줘",
      "DCF 샘플로 평가하고 엑셀까지 만들어줘",
    ].forEach(add);
    return [["지금 대화에서 이어가기", pri]];
  };

  // ── 사이드바 동작 ──
  const onNewChat = () => {
    saveCurrent();
    resetToNew();
    navigate("/");
    collapseSidebarOnMobile();
  };
  const onNewChatInGroup = (group: string) => {
    saveCurrent();
    resetToNew();
    currentGroupRef.current = group; // 이 대화가 저장될 때 이 그룹 소속이 된다
    navigate("/");
    collapseSidebarOnMobile();
  };
  const onOpenSession = (id: string) => {
    // 이미 보고 있는 대화를 다시 눌러도 오버레이는 닫는다 — 앱처럼, 선택했으면 본문으로
    collapseSidebarOnMobile();
    if (id === activeRef.current && !libraryOpen) return;
    setLibraryOpen(false);
    if (id === activeRef.current) return;
    navigate(`/c/${encodeURIComponent(id)}`);
  };
  const onDeleteSession = (id: string) => {
    const s = sessions.find((x) => x.id === id);
    if (!window.confirm("이 대화를 삭제할까요?" + (s && s.title ? "\n\n" + s.title : "")))
      return;
    useChat.getState().deleteSession(id);
    if (id === activeRef.current || id === convIdRef.current) {
      resetToNew();
      navigate("/", { replace: true });
    }
  };
  // 사이드바에서 활성 대화의 이름을 바꾸면 상단 제목·잠금 동기화 (바닐라 commitRename)
  const onSessionRenamed = (id: string, newTitle: string) => {
    if (id === activeRef.current || id === convIdRef.current) {
      titleLockedRef.current = true;
      setTitleBoth(newTitle);
    }
  };
  const onOpenLibrary = () => {
    if (libraryOpen) {
      navigate(activeRef.current ? `/c/${encodeURIComponent(activeRef.current)}` : "/");
      return;
    }
    navigate("/library");   // 열림 상태는 경로 동기화 effect 가 처리
    collapseSidebarOnMobile();
  };

  // ── 칩 클릭 ──
  const pickChoice = (turnIdx: number, text: string) => {
    const t = turnsRef.current[turnIdx];
    if (t && t.role === "agent") t.choices = null; // 고른 내용은 유저 말풍선으로 남는다
    choicesOpenRef.current = false;
    commit();
    void send(text);
  };
  const pickNext = (turnIdx: number, text: string) => {
    const t = turnsRef.current[turnIdx];
    if (t && t.role === "agent") {
      t.next = [];
      t.nextFinal = false;
    }
    commit();
    void send(text);
  };
  const toggleProgress = (turnIdx: number) => {
    const t = turnsRef.current[turnIdx];
    if (t && t.role === "agent") {
      t.progress.open = !t.progress.open;
      commit();
    }
  };

  // ── 패널 토글 (정보 패널 ↔ 통합 우측 패널 배타) ──
  const togglePanel = () => {
    setPreviewFile(null); // 기존 동작 유지 — 정보 패널 토글 시 미리보기는 버린다
    dispatchPanel({ type: "close" });
    const off = !panelOff;
    setPanelOff(off);
    try {
      localStorage.setItem("va_panel_off", off ? "1" : "0");
    } catch {
      /* noop */
    }
  };
  // 탑바 격자 아이콘 — 통합 패널 토글 (내용이 결과든 미리보기든 있으면 동작)
  const toggleResults = () => {
    dispatchPanel({ type: "toggle", content: panelContent });
  };

  // ── qnav 점프 ──
  const jumpToQuestion = (i: number) => {
    const arts = listRef.current?.querySelectorAll("article.message.user");
    const art = arts?.[i] as HTMLElement | undefined;
    const scroll = scrollRef.current;
    if (!art || !scroll) return;
    scroll.scrollTo({ top: art.offsetTop - scroll.offsetTop - 12, behavior: "smooth" });
    setQnavActive(i);
  };

  // ── 파일 드래그앤드롭 (바닐라 dropZone=.chat-card) ──
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer && e.dataTransfer.files.length) {
      setPendingFiles([...pendingFilesRef.current, ...Array.from(e.dataTransfer.files)]);
    }
  };

  // ── 상단 제목 이름 변경 (바닐라 startRename "title" 모드) ──
  const finishTitleRename = (value: string, save: boolean) => {
    setTitleRenaming(false);
    if (!save) return;
    const next = (value || "").trim().slice(0, 60);
    const id = activeRef.current;
    if (!next || !id) return;
    useChat.getState().renameSession(id, next);
    titleLockedRef.current = true;
    setTitleBoth(next);
  };

  // ── 테마 (바닐라 applyTheme — light/ivory/dark 순환) ──
  const cycleTheme = () => {
    const cur = document.documentElement.getAttribute("data-theme") || "light";
    const next =
      THEME_ORDER[(THEME_ORDER.indexOf(cur as (typeof THEME_ORDER)[number]) + 1) %
        THEME_ORDER.length];
    if (next === "light") document.documentElement.removeAttribute("data-theme");
    else document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem("va_theme", next);
      localStorage.setItem("va_theme_v2", "1"); // 바닐라 마이그레이션 플래그와 동일
    } catch {
      /* noop */
    }
    setTheme(next);
    VAChart.refresh(); // 차트 팔레트·축 색을 새 테마 토큰으로
  };

  const rootClass =
    "chat-root" +
    (isEmpty ? " is-empty" : "") +
    (hasConv ? " has-conv" : "") +
    (panelOff ? " panel-off" : "") +
    (panelVisible ? " results-open" : "") +
    (libraryOpen ? " library-open" : "") +
    (sideResizing ? " side-resizing" : "") +
    (rpResizing ? " rp-resizing" : "");

  return (
    <div className={rootClass}>
      <div className={shellClass(sideCollapsed, railSettled, sideAnimating)}>
        <Sidebar
          sessions={sessions}
          activeId={activeSessionId}
          writingIds={writingIds}
          doneIds={doneIds}
          busy={busy}
          onNew={onNewChat}
          onNewInGroup={onNewChatInGroup}
          onOpen={onOpenSession}
          onDelete={onDeleteSession}
          onOpenLibrary={onOpenLibrary}
          libraryOpen={libraryOpen}
          railMode={railSettled}
          onCollapse={() => setSideCollapsedPersist(true)}
          onExpand={() => setSideCollapsedPersist(false)}
          onResizingChange={setSideResizing}
          onRenamed={onSessionRenamed}
          onNavigated={collapseSidebarOnMobile}
        />
        <main className="main">
          <header className="topbar">
            <div className="chat-title-wrap">
              {titleRenaming ? (
                <input
                  className="title-rename"
                  defaultValue={title}
                  maxLength={60}
                  aria-label="대화 이름"
                  autoFocus
                  onFocus={(e) => e.currentTarget.select()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      finishTitleRename(e.currentTarget.value, true);
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      finishTitleRename("", false);
                    }
                  }}
                  onBlur={(e) => finishTitleRename(e.currentTarget.value, true)}
                />
              ) : (
                <div className="chat-title">{title}</div>
              )}
              <button
                className="title-menu-btn"
                type="button"
                title="대화 메뉴"
                aria-label="대화 메뉴"
                aria-haspopup="menu"
                aria-expanded={!!titleMenuAnchor}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!activeSessionId) return;
                  const anchor = e.currentTarget;
                  setTitleMenuAnchor((a) => (a ? null : anchor));
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="5" r="1.4" fill="currentColor" stroke="none" />
                  <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
                  <circle cx="12" cy="19" r="1.4" fill="currentColor" stroke="none" />
                </svg>
              </button>
              {titleMenuAnchor && activeSessionId && (
                <SessionMenu
                  id={activeSessionId}
                  anchor={titleMenuAnchor}
                  onClose={() => setTitleMenuAnchor(null)}
                  onRenameStart={() => setTitleRenaming(true)}
                  onDelete={onDeleteSession}
                />
              )}
            </div>
            <div className="topbar-actions">
              <Button
                variant="icon"
                active={panelVisible}
                id="resultsToggle"
                hidden={!panelContent.hasResults && !panelContent.hasPreview}
                title="결과 패널 열기/닫기"
                aria-label="결과 패널 열기/닫기"
                onClick={toggleResults}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3v18" />
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M21 9H3" />
                  <path d="M21 15H3" />
                </svg>
              </Button>
              <Button
                variant="icon"
                active={!panelOff}
                id="panelToggle"
                title="정보 패널 열기/닫기"
                aria-label="정보 패널 열기/닫기"
                onClick={togglePanel}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="16" rx="2" />
                  <path d="M15 4v16" />
                </svg>
              </Button>
              <Button
                variant="icon"
                title={`테마: ${THEME_LABEL[theme] || "라이트"} (클릭하여 전환)`}
                aria-label="테마 전환"
                onClick={cycleTheme}
              >
                {theme === "dark" ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
                  </svg>
                )}
              </Button>
            </div>
          </header>

          <section className="workspace">
            {libraryOpen && (
              <LibraryView
                sessions={sessions}
                onOpenChat={(convId) => {
                  setLibraryOpen(false);
                  if (convId.startsWith("/")) {
                    navigate(convId);           // lab 딥링크 (/buffett?..., /peer)
                    return;
                  }
                  if (convId !== activeRef.current)
                    navigate(`/c/${encodeURIComponent(convId)}`);
                }}
                onPreview={openPreview}
              />
            )}
            <div className="chat-card" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
              {qnavItems.length >= 2 && (
                <nav className="qnav" aria-label="질문 목록">
                  {qnavItems.map((it, i) => (
                    <button
                      key={i}
                      className={`qnav-item${i === qnavActive ? " active" : ""}`}
                      type="button"
                      aria-label={it.q}
                      onClick={() => jumpToQuestion(i)}
                    >
                      <span className="qnav-tip">
                        <b>{it.q}</b>
                        <em>{it.sum}</em>
                      </span>
                      <i />
                    </button>
                  ))}
                </nav>
              )}
              <div
                className="chat-scroll"
                ref={scrollRef}
                onScroll={() => {
                  stickRef.current = nearBottom();
                  setShowJump(!stickRef.current);
                }}
                onWheel={(e) => {
                  // 바닥 근처에서 살짝 올리는 정도로도 즉시 고정을 푼다 (사용자 의도 감지)
                  if (e.deltaY < 0) {
                    stickRef.current = false;
                    setShowJump(true);
                  }
                }}
                onTouchStart={(e) => {
                  touchYRef.current = e.touches[0]?.clientY ?? null;
                }}
                onTouchMove={(e) => {
                  const y = e.touches[0]?.clientY ?? null;
                  if (touchYRef.current != null && y != null && y > touchYRef.current + 4) {
                    stickRef.current = false;
                    setShowJump(true);
                  }
                  if (y != null) touchYRef.current = y;
                }}
              >
                {isEmpty && isGraphChat ? (
                  <div className="empty-hero">
                    <div className="hello">Graph DB 챗</div>
                    <h2 className="hello-sub">모든 답변이 DeepSearch Graph DB 도구만으로 만들어집니다</h2>
                    <div className="hero-beta-note">
                      * 답변 품질 점검(QC)용 베타입니다. Graph DB 커버리지 밖 질문에는 &lsquo;조회되지 않음&rsquo;으로 답하며,
                      각 답변의 도구 호출 과정은 &lsquo;이전 단계 보기&rsquo;에서 확인할 수 있습니다.
                    </div>
                    <div className="hero-composer-slot">
                      <Composer
                        busy={busy}
                        onSend={(t) => void send(t)}
                        onStop={stop}
                        hero
                        files={pendingFiles}
                        onFilesChange={setPendingFiles}
                        getQuickGroups={() => []}
                      />
                    </div>
                    <div className="qcards" style={{ gridTemplateColumns: "1fr" }}>
                      <div className="qcard">
                        <div className="qcard-title"><span>Graph DB 로 물어보기</span></div>
                        {GRAPH_CHIPS.map((q) => (
                          <button key={q} className="chip qrow" type="button"
                            onClick={() => void send(q)}>
                            <span>{q}</span>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                              strokeLinecap="round" strokeLinejoin="round">
                              <path d="M5 12h14" />
                              <path d="m12 5 7 7-7 7" />
                            </svg>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : isEmpty ? (
                  <div className="empty-hero">
                    <div className="hello" title={greetingTitle(greet)}>{greet.text}</div>
                    <h2 className="hello-sub">오늘은 어떤 기업이 궁금하신가요?</h2>
                    <div className="hero-beta-note">
                      * 밸류에이션에이전트는 현재 베타서비스 중으로 이용 중 업데이트가 이루어질 수 있습니다.
                    </div>
                    <div className="hero-composer-slot">
                      <Composer
                        busy={busy}
                        onSend={(t) => void send(t)}
                        onStop={stop}
                        hero
                        files={pendingFiles}
                        onFilesChange={setPendingFiles}
                        getQuickGroups={getQuickGroups}
                        hoverSuggestion={hoverQ}
                        onConsumeHover={() => setHoverQ("")}
                        onHoverChip={setHoverQ}
                      />
                    </div>
                    <div className="qcards">
                      {HERO_CARDS.map(([cardTitle, chips]) => {
                        const isDiscovery = cardTitle === "기업 발굴";
                        const shown = isDiscovery && discoveryAlt ? DISCOVERY_ALT : chips;
                        return (
                        <div className="qcard" key={cardTitle as string}>
                          <div className="qcard-title">
                            <span>{cardTitle as string}</span>
                            {isDiscovery && (
                              <button
                                className="qcard-shuffle"
                                type="button"
                                title={discoveryAlt ? "원래 질문 보기" : "다른 질문 보기"}
                                aria-label={discoveryAlt ? "원래 질문 보기" : "다른 질문 보기"}
                                onClick={() => setDiscoveryAlt((v) => !v)}
                              >
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="m18 14 4 4-4 4" />
                                  <path d="m18 2 4 4-4 4" />
                                  <path d="M2 18h1.973a4 4 0 0 0 3.3-1.7l5.454-8.6a4 4 0 0 1 3.3-1.7H22" />
                                  <path d="M2 6h1.972a4 4 0 0 1 3.6 2.2" />
                                  <path d="M22 18h-6.041a4 4 0 0 1-3.3-1.8l-.359-.45" />
                                </svg>
                              </button>
                            )}
                          </div>
                          {(shown as Array<string | { label: string; href: string }>)
                            .filter((raw) => typeof raw === "string" || !isEnterpriseOnlyPath(raw.href) || isEnterpriseUser(user))
                            .map(
                            (raw) => {
                              const q = fillDecliner(raw, declinerName);
                              const isLink = typeof q !== "string";
                              const label = isLink ? q.label : q;
                              return (
                                <button
                                  key={label}
                                  className="chip qrow"
                                  type="button"
                                  onMouseEnter={() => { if (!isLink) armHoverQ(q); }}
                                  onMouseLeave={cancelHoverQ}
                                  onClick={() => {
                                    cancelHoverQ();
                                    if (isLink) navigate(q.href);
                                    else void send(q);
                                  }}
                                >
                                  <span>{label}</span>
                                  <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M5 12h14" />
                                    <path d="m12 5 7 7-7 7" />
                                  </svg>
                                </button>
                              );
                            },
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <MessageList
                    turns={turns}
                    restoredHtml={restoredHtml}
                    busy={busy}
                    listRef={listRef}
                    onSend={(t) => void send(t)}
                    onToggleProgress={toggleProgress}
                    onPickChoice={pickChoice}
                    onPickNext={pickNext}
                    onPanelOpen={(i) => {
                      if (i >= 0 && i < rpSpecs.length) setRpIdx(i);
                      dispatchPanel({ type: "results-arrived" }); // 오픈 + 결과 탭
                    }}
                    onPreviewOpen={openPreview}
                    uploadUrlOf={(name) => uploadUrlsRef.current[name]}
                    onOpenUpload={(url, name) => openPreview({ url, name, kind: "excel" })}
                  />
                )}
              </div>

              <button
                className={`jump-bottom${showJump && !isEmpty ? " show" : ""}`}
                type="button"
                title="맨 아래로"
                aria-label="맨 아래로"
                onClick={() => scrollDown(true)}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 5v14" />
                  <path d="M6 13l6 6 6-6" />
                </svg>
              </button>

              {!isEmpty && (
                <div className="composer-dock">
                  {busy && !turns.some((t) => t.role === "agent" && t.live) && (
                    <div className="bg-writing-note" role="status">
                      <span className="conv-writing" aria-hidden />
                      응답을 백그라운드에서 작성 중이에요 — 완료되면 자동으로 갱신됩니다
                    </div>
                  )}
                  {queuedMsgs.length > 0 && (
                    <div className="queued-strip" role="status"
                         aria-label={`대기 중인 질문 ${queuedMsgs.length}개`}>
                      <div className="queued-label">답변이 끝나면 이어서 보냅니다</div>
                      {queuedMsgs.map((q, i) => (
                        <div className="queued-bubble" key={`${i}-${q.text.slice(0, 20)}`}>
                          <button
                            type="button"
                            className="queued-text"
                            title={busy ? "답변이 끝나면 자동으로 보냅니다" : "눌러서 지금 보내기"}
                            onClick={() => sendQueuedNow(i)}
                          >
                            {q.text}
                            {q.nFiles ? <span className="queued-files">첨부 {q.nFiles}</span> : null}
                          </button>
                          <button type="button" className="queued-remove" aria-label="대기열에서 빼기"
                                  onClick={() => removeQueued(i)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                                 strokeLinecap="round"><path d="M18 6L6 18" /><path d="M6 6l12 12" /></svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <Composer
                    busy={busy}
                    onSend={(t) => void send(t)}
                    onStop={stop}
                    files={pendingFiles}
                    onFilesChange={setPendingFiles}
                    getQuickGroups={getQuickGroups}
                    hoverSuggestion={hoverQ}
                    onConsumeHover={() => setHoverQ("")}
                    onHoverChip={setHoverQ}
                  />
                </div>
              )}
            </div>

            <InfoPanel
              targets={targets}
              activeTargetIdx={activeTargetIdx}
              onPickTarget={setActiveTargetIdx}
              traceGroups={traceGroups}
            />
          </section>

          {/* 통합 우측 패널 — 활성 탭의 내용 하나만 마운트, 탭 바는 둘 다 있을 때만 주입 */}
          {panelVisible &&
            (() => {
              const tabBar = showTabBar(panelContent) ? (
                <div className="rp-tabs" role="tablist" aria-label="우측 패널 탭">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={panelTab === "results"}
                    className={`rp-tab${panelTab === "results" ? " active" : ""}`}
                    onClick={() => dispatchPanel({ type: "set-tab", tab: "results" })}
                  >
                    스크리닝 결과
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={panelTab === "preview"}
                    className={`rp-tab${panelTab === "preview" ? " active" : ""}`}
                    onClick={() => dispatchPanel({ type: "set-tab", tab: "preview" })}
                  >
                    파일 미리보기
                  </button>
                </div>
              ) : null;
              return panelTab === "preview" && previewFile ? (
                <FilePreviewPanel
                  file={previewFile}
                  onClose={closePreview}
                  onResizingChange={setRpResizing}
                  tabs={tabBar}
                />
              ) : (
                <ResultsPanel
                  specs={rpSpecs}
                  idx={rpIdx >= 0 && rpIdx < rpSpecs.length ? rpIdx : rpSpecs.length - 1}
                  onPickIdx={setRpIdx}
                  onClose={() => dispatchPanel({ type: "close" })}
                  onAsk={(q) => void send(q)}
                  busy={busy}
                  onResizingChange={setRpResizing}
                  onExcelPreview={(url, name) =>
                    openPreview({ url, name, kind: "excel" })
                  }
                  tabs={tabBar}
                />
              );
            })()}
          <ContactFooter />
        </main>
      </div>
      <SideReopen collapsed={sideCollapsed} onOpen={() => setSideCollapsedPersist(false)} />
    </div>
  );
}
